import { Autocomplete, Box, Button, Chip, Stack, TextField } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useEffect } from 'react'
import { DatePicker, TimePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import Header from 'components/Header'
import { useBasePath } from 'components/Providers'
import { saveLog, destroyLog } from 'store/mentorship/log'
import { getTypes } from 'store/lookup'

const schema = yup.object().shape({
  logDate: yup.string().required('Required'),
  content: yup.string().required('Required'),
})

export default function MyLogsForm({ log, mentorshipId }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const basePath = useBasePath()
  const allTypes = useSelector(state => state.lookup.types)
  const standards = allTypes?.filter(type => type.category === 'mentorshipStandard') || []

  useEffect(() => {
    if (!allTypes) dispatch(getTypes())
  }, [dispatch, Boolean(allTypes)])

  const { control, handleSubmit } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      logDate: log?.logDate || '',
      startTime: log?.startTime || '',
      endTime: log?.endTime || '',
      content: log?.content || '',
      standardCodes: log?.standardCodes || [],
    },
  })

  const onSubmit = async data => {
    const payload = { ...data, mentorshipId, status: 'submitted' }
    if (log?.id) payload.id = log.id
    const result = await dispatch(saveLog(payload))
    if (result.payload) {
      navigate(basePath)
    }
  }

  const handleDelete = async () => {
    await dispatch(destroyLog({ mentorshipId, id: log.id }))
    navigate(basePath)
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Header title={log?.id ? t('Edit Log') : t('New Log')} small close borderBottom />
      <Stack spacing={2} sx={{ p: 2 }}>
        <Controller
          name="logDate"
          control={control}
          render={({ field, fieldState }) => (
            <DatePicker
              sx={fieldState.error ? { label: { color: 'red' } } : {}}
              slotProps={{ actionBar: { actions: ['clear'] }, textField: { size: 'small' }, field: { fullWidth: true } }}
              label={t('Log Date')}
              {...field}
              value={field.value ? dayjs(field.value) : null}
              onChange={date => field.onChange(date ? date.format('YYYY-MM-DD') : '')}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <Stack direction="row" spacing={2}>
          <Controller
            name="startTime"
            control={control}
            render={({ field: { value, onChange } }) => (
              <TimePicker
                sx={{ flex: 1 }}
                slotProps={{ textField: { size: 'small' } }}
                label={t('Start Time')}
                value={value ? dayjs(value, 'HH:mm') : null}
                onChange={time => onChange(time ? time.format('HH:mm:ss') : '')}
              />
            )}
          />
          <Controller
            name="endTime"
            control={control}
            render={({ field: { value, onChange } }) => (
              <TimePicker
                sx={{ flex: 1 }}
                slotProps={{ textField: { size: 'small' } }}
                label={t('End Time')}
                value={value ? dayjs(value, 'HH:mm') : null}
                onChange={time => onChange(time ? time.format('HH:mm:ss') : '')}
              />
            )}
          />
        </Stack>

        <Controller
          name="content"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              multiline
              rows={6}
              label={t('Comment')}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="standardCodes"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              multiple
              options={standards}
              getOptionLabel={o => `[${o.code || o.name}] - ${o.name}`}
              isOptionEqualToValue={(o, v) => o.code === v || o.code === v?.code}
              value={standards.filter(s => (value || []).includes(s.code || s.name))}
              onChange={(_, vals) => onChange(vals.map(v => v.code || v.name))}
              renderTags={(vals, getTagProps) =>
                vals.map((opt, idx) => (
                  <Chip
                    label={`[${opt.code || opt.name}] - ${opt.name}`}
                    {...getTagProps({ index: idx })}
                    key={opt.code || opt.name}
                    size="small"
                  />
                ))
              }
              renderInput={params => <TextField {...params} label={t('Standards')} />}
            />
          )}
        />

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            {log?.id && (
              <Button variant="outlined" color="error" onClick={handleDelete}>
                {t('Delete')}
              </Button>
            )}
            <Button type="submit" variant="contained">
              {t('Save')}
            </Button>
          </Box>
        </Box>
      </Stack>
    </Box>
  )
}
